import type { Command } from "./commands.ts";
import { HISTORY_CAP, type History } from "./history.ts";

/**
 * Where the history stood when the document was last written (ADR-070 decision 8), so the save state is read
 * from the history's position and never by comparing documents. `top` is the command then on top of the undo
 * list, or null for an empty list; `dropped` is how many commands the cap had trimmed from the bottom of the
 * list by then, which tells an empty list at the save from one emptied by undoing after a trim. Commands are
 * compared by identity: each edit records a command of its own, so a command undone and another made in its
 * place never read as the same.
 */
export interface SavePoint {
  readonly top: Command | null;
  readonly dropped: number;
}

/**
 * The command on top of the undo list.
 *
 * @param history The history.
 * @returns The newest command still undoable, or null when there is none.
 */
function topOf(history: History): Command | null {
  return history.past[history.past.length - 1] ?? null;
}

/**
 * The history's position now, to be recorded once the document taken with it has been written.
 *
 * @param history The history.
 * @param dropped The commands the cap has trimmed so far.
 * @returns The position.
 */
export function savePointOf(history: History, dropped: number): SavePoint {
  return { top: topOf(history), dropped };
}

/**
 * Whether the document is the one written at a save point: the undo list's top is the command it was then,
 * and, for a save of an empty list, no command has been trimmed below it since.
 *
 * @param history The history.
 * @param dropped The commands the cap has trimmed so far.
 * @param point The last save's position.
 * @returns True while the state reads saved.
 */
export function atSavePoint(history: History, dropped: number, point: SavePoint): boolean {
  return topOf(history) === point.top && (point.top !== null || point.dropped === dropped);
}

/**
 * How many commands executing one more drops from the bottom of the list: one when the list is at the cap.
 *
 * @param before The history before the command is executed.
 * @returns 1 or 0.
 */
export function droppedByExecute(before: History): number {
  return before.past.length >= HISTORY_CAP ? 1 : 0;
}
